'use client';

import React from 'react';
import { ThreeDots } from './ThreeDots';
import styles from './Button.module.scss';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'fusion-md';
export type ButtonVariant =
  | 'default'
  | 'flat'
  | 'cta'
  | 'flat-black'
  | 'light-gray'
  | 'flat-blue'
  | 'dark-blue'
  | 'flat-light-blue'
  | 'flat-light-blue-outlined'
  | 'flat-red'
  | 'flat-orange'
  | 'flat-ind-blue'
  | 'primary'
  | 'fusion-outline'
  | 'white-outline'
  | 'fusion-secondary'
  | 'fusion-white'
  | 'danger'
  | 'danger-filled'
  | 'success-filled'
  | 'warning'
  | 'success';
export type ButtonLayout = 'full' | null;

interface Props {
  as?: React.ElementType;
  children?: React.ReactNode;
  className?: string;
  href?: string;
  isLoading?: boolean;
  layout?: ButtonLayout;
  loadingDotsColor?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  size?: ButtonSize;
  style?: React.CSSProperties;
  type?: string;
  variant?: ButtonVariant;
}

export type ButtonProps = Props & Omit<React.ComponentProps<'button'> & React.ComponentProps<'a'>, keyof Props>;

const LOADING_DOTS_COLOR: Record<ButtonVariant, string> = {
  cta: '#192940',
  danger: '#f98080',
  'danger-filled': '#ffffff',
  'dark-blue': '#76a9fa',
  default: '#192940',
  flat: '#192940',
  'flat-black': '#000000',
  'flat-blue': '#76a9fa',
  'flat-ind-blue': '#24344f',
  'flat-light-blue': '#76a9fa',
  'flat-light-blue-outlined': '#76a9fa',
  'flat-orange': '#ff5a1f',
  'flat-red': '#f98080',
  'fusion-outline': '#3f83f8',
  'fusion-secondary': '#3f83f8',
  'fusion-white': '#3f83f8',
  'light-gray': '#192940',
  primary: '#3f83f8',
  success: '#192940',
  'success-filled': '#ffffff',
  warning: '#192940',
  'white-outline': '#ffffff',
};

export const ButtonContainer: React.FC<ButtonProps> = ({ as: Tag = 'button', children, className, ...props }) => (
  <Tag className={`${styles.button}${className ? ` ${className}` : ''}`} {...props}>
    {children}
  </Tag>
);

export const Button: React.FC<ButtonProps> = ({
  children,
  className,
  isLoading = false,
  layout,
  onClick,
  size = 'md',
  style,
  type = 'button',
  variant = 'default',
  ...otherProps
}) => {
  const additionalClassNames = className || '';
  const classNames = `${variant} ${size} ${additionalClassNames} ${layout ? layout : ''}`.trim();
  const loadingDotsColor = LOADING_DOTS_COLOR[variant] ?? '#192940';

  return (
    <ButtonContainer {...otherProps} className={classNames} onClick={onClick} style={style} type={type}>
      {isLoading ? (
        <div className="inline-flex justify-center text-center w-full">
          <ThreeDots color={loadingDotsColor} height={16} width={30} />
        </div>
      ) : (
        <span>{children}</span>
      )}
    </ButtonContainer>
  );
};
